import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

export const fetchActiveFinancialYear = createAsyncThunk(
  "financialYear/fetchActiveFinancialYear",
  async (dbCode, { rejectWithValue }) => {
    try {
      if (!dbCode) return rejectWithValue("No dbCode provided");
      const response = await axios.get(`${apiBaseUrl}/financialyear/getActiveFinancialYear/${dbCode}`);
      if (response.data && response.data.success) {
        return response.data.data;
      }
      return rejectWithValue("Failed to fetch active financial year");
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAllFinancialYears = createAsyncThunk(
  "financialYear/fetchAllFinancialYears",
  async (dbCode, { rejectWithValue }) => {
    try {
      if (!dbCode) return rejectWithValue("No dbCode provided");
      const response = await axios.get(`${apiBaseUrl}/financialyear/getFinancialYears/${dbCode}`);
      if (response.data && response.data.success) {
        return response.data.data;
      }
      return rejectWithValue("Failed to fetch financial years list");
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const getInitialActiveFy = () => {
  try {
    const stored = sessionStorage.getItem("userSelectedFy");
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed && (parsed.Fyid || parsed.fyid)) {
        return {
          fyid: Number(parsed.Fyid || parsed.fyid || 0),
          fromDate: parsed.Frmdate || parsed.FromDate || parsed.fromDate || null,
          toDate: parsed.Todate || parsed.ToDate || parsed.toDate || null,
          status: parsed.Status || parsed.status || "Active",
          narration: parsed.Narration || parsed.narration || "",
        };
      }
    }
  } catch (e) {
    console.error("Error reading stored activeFy:", e);
  }
  return {
    fyid: 0,
    fromDate: null,
    toDate: null,
    status: "Active",
    narration: "",
  };
};

const initialState = {
  activeFy: getInitialActiveFy(),
  allYears: [],
  loading: false,
  error: null,
};

const financialYearSlice = createSlice({
  name: "financialYear",
  initialState,
  reducers: {
    setActiveFinancialYear: (state, action) => {
      if (action.payload) {
        const fyidVal = Number(action.payload.Fyid ?? action.payload.fyid ?? state.activeFy?.fyid ?? 0);
        const updated = {
          ...state.activeFy,
          ...action.payload,
          fyid: fyidVal,
          fromDate: action.payload.Frmdate || action.payload.FromDate || action.payload.fromDate || state.activeFy?.fromDate,
          toDate: action.payload.Todate || action.payload.ToDate || action.payload.toDate || state.activeFy?.toDate,
        };
        state.activeFy = updated;
        sessionStorage.setItem("userSelectedFy", JSON.stringify(updated));
      }
    },
    setAllFinancialYears: (state, action) => {
      state.allYears = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchActiveFinancialYear.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchActiveFinancialYear.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          const currentDb = action.meta?.arg;
          const stored = sessionStorage.getItem("userSelectedFy");
          if (stored) {
            try {
              const parsed = JSON.parse(stored);
              if (parsed && (parsed.Fyid || parsed.fyid)) {
                // Only retain stored FY if it explicitly matches the currently active database
                if (parsed.dbCode && parsed.dbCode === currentDb) {
                  return;
                }
              }
            } catch (_) {}
          }
          const payloadFyid = Number(action.payload.Fyid ?? action.payload.fyid ?? action.payload.ID ?? action.payload.id ?? 0);
          const active = {
            dbCode: currentDb,
            fyid: payloadFyid,
            fromDate: action.payload.Frmdate || action.payload.FromDate || action.payload.fromDate,
            toDate: action.payload.Todate || action.payload.ToDate || action.payload.toDate,
            status: action.payload.Status || action.payload.status || "Active",
            narration: action.payload.Narration || "",
          };
          state.activeFy = active;
          sessionStorage.setItem("userSelectedFy", JSON.stringify(active));
        }
      })
      .addCase(fetchActiveFinancialYear.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchAllFinancialYears.fulfilled, (state, action) => {
        state.allYears = action.payload || [];
      });
  },
});

export const { setActiveFinancialYear, setAllFinancialYears } = financialYearSlice.actions;

export const selectActiveFyid = (state) => Number(state.financialYear?.activeFy?.fyid) || 0;
export const selectActiveFy = (state) => state.financialYear?.activeFy;
export const selectAllFinancialYears = (state) => state.financialYear?.allYears || [];

export default financialYearSlice.reducer;
